/**
 * Componente Header - Encabezado de la aplicación
 *
 * Dos barras como la web oficial de MET Energía España:
 * - Barra superior blanca con logo
 * - Barra de navegación navy oscuro
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
} from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { COLORS } from '../theme/theme';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (location.pathname === '/login') {
    return null;
  }

  return (
    <Box>
      {/* Barra superior blanca - Logo y marca */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: '#ffffff',
          boxShadow: 'none',
          borderBottom: '1px solid #e8ecf0',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 64 } }}>
            {/* Logo MET */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              <img
                src="/logo-met.png"
                alt="MET Energía"
                style={{ height: 36 }}
              />
            </Box>

            {/* Separador y nombre */}
            <Typography
              sx={{
                ml: 3,
                pl: 3,
                borderLeft: '1px solid #d0d5dd',
                color: COLORS.navy,
                fontWeight: 400,
                fontSize: '0.85rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              <strong>MET</strong>ENERGÍA ESPAÑA
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            {/* Botones estilo MET con gradiente */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  background: COLORS.gradientElectricidad,
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                }}
              >
                LICITATOR
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Barra de navegación navy - como la web MET */}
      {isAuthenticated && (
        <AppBar
          position="static"
          sx={{
            backgroundColor: COLORS.teal,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar
              disableGutters
              sx={{ minHeight: { xs: 50, md: 56 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  startIcon={<HomeIcon sx={{ fontSize: '18px !important' }} />}
                  onClick={() => navigate('/')}
                  sx={{
                    color: '#ffffff',
                    opacity: isActive('/') ? 1 : 0.7,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    px: 2.5,
                    py: 0.8,
                    borderBottom: isActive('/') ? '2px solid #ffffff' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  Inicio
                </Button>

                <Button
                  startIcon={<SearchIcon sx={{ fontSize: '18px !important' }} />}
                  onClick={() => navigate('/analizar')}
                  sx={{
                    color: '#ffffff',
                    opacity: isActive('/analizar') ? 1 : 0.7,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    px: 2.5,
                    py: 0.8,
                    borderBottom: isActive('/analizar') ? '2px solid #ffffff' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  Analizar
                </Button>

                <Button
                  startIcon={<DescriptionIcon sx={{ fontSize: '18px !important' }} />}
                  onClick={() => navigate('/generar-propuesta')}
                  sx={{
                    color: '#ffffff',
                    opacity: isActive('/generar-propuesta') ? 1 : 0.7,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    px: 2.5,
                    py: 0.8,
                    borderBottom: isActive('/generar-propuesta') ? '2px solid #ffffff' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  Generar Oferta
                </Button>
              </Box>

              <Box sx={{ flexGrow: 1 }} />

              <Button
                startIcon={<LogoutIcon sx={{ fontSize: '16px !important' }} />}
                onClick={handleLogout}
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  px: 2,
                  borderRadius: 0,
                  '&:hover': {
                    color: '#ffffff',
                    backgroundColor: 'rgba(229, 57, 53, 0.15)',
                  },
                }}
              >
                Salir
              </Button>
            </Toolbar>
          </Container>
        </AppBar>
      )}
    </Box>
  );
};

export default Header;
